import React, { useEffect, useState, useRef } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from "@/components/ui/button";import { PageLoader } from '@/components/ui/page-loader';import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { BASE_URL } from '../config/constants';
import { toast } from 'sonner';
import { Cropper } from '@/components/ui/image-crop';
import { Upload, RefreshCw, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';


interface AboutSection {
  _id: string;
  heading: string;
  subtitle1: string;
  content: string;
  aboutImage: string;
  aboutImageActive: boolean;
  isActive: boolean;
  order: number;
}

interface AboutSectionManagerProps {
  domain: string;
}

const emptyAboutSection: Omit<AboutSection, '_id'> = {
  heading: '',
  subtitle1: '',
  content: '',
  aboutImage: '',
  aboutImageActive: true,
  isActive: true,
  order: 0,
};

const AboutSectionManager: React.FC<AboutSectionManagerProps> = ({ domain }) => {
  const [aboutSection, setAboutSection] = useState<AboutSection | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyAboutSection);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadStep, setUploadStep] = useState<'idle' | 'selecting' | 'cropping'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cropPercent, setCropPercent] = useState<any>(null);
  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subtitleRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);


  // Auto-resize textarea based on content
  const adjustTextareaHeight = (textarea: HTMLTextAreaElement | null, minHeight: number = 120) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    }
  };

  const fetchAboutSection = () => {
    setLoading(true);
    axios.get(BASE_URL + '/api/about-section')
      .then(res => {
        setAboutSection(res.data);
        setForm(res.data);
      })
      .catch(() => {
        setAboutSection(null);
        setForm(emptyAboutSection);
      })
      .finally(() => setLoading(false));
  };

  const resetUploadState = () => {
    setUploadStep('idle');
    setSelectedFile(null);
    setCropPercent(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const openCropperForExisting = () => {
    if (form.aboutImage) {
      fetch(form.aboutImage)
        .then(res => res.blob())
        .then(blob => {
          const file = new File([blob], 'about-image.png', { type: blob.type });
          setSelectedFile(file);
          setUploadStep('cropping');
        })
        .catch(() => {
          toast.error('Failed to load image for editing');
        });
    }
  };

  useEffect(() => {
    if (domain) {
      fetchAboutSection();
    }
  }, [domain]);

  // Adjust textarea heights when content changes (including initial load)
  useEffect(() => {
    adjustTextareaHeight(subtitleRef.current, 56); // 2 lines minimum
  }, [form.subtitle1]);

  useEffect(() => {
    adjustTextareaHeight(contentRef.current, 140); // 6 lines minimum
  }, [form.content]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setForm(prev => ({ ...prev, [name]: newValue }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validFormats = ['image/png', 'image/jpeg'];
    const maxSize = 2 * 1024 * 1024;
    
    if (!validFormats.includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      toast.error('Only .png and .jpeg files are allowed.');
      return;
    }
    
    if (file.size > maxSize) {
      setError('File size must be less than 2MB.');
      toast.error('File size must be less than 2MB.');
      return;
    }
    
    setError('');
    setSelectedFile(file);
    setUploadStep('cropping');
  };

  const handleCropComplete = async (_croppedImage: string) => {
    // Callback when user adjusts crop - just store the notification that we're ready
    // The actual upload happens when user clicks the Upload button
  };

  const hasChanges = (): boolean => {
    if (!aboutSection) return true;
    return JSON.stringify(form) !== JSON.stringify(aboutSection);
  };

  const handleReset = () => {
    if (aboutSection) {
      setForm({ ...aboutSection });
    } else {
      setForm(emptyAboutSection);
    }
    resetUploadState();
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const submitData = { ...form };
      
      if ('_id' in submitData) {
        delete (submitData as any)._id;
      }
      
      let savedData;
      if (aboutSection?._id) {
        const res = await axios.put(`${BASE_URL}/api/about-section/${aboutSection._id}`, submitData);
        savedData = res.data.data || res.data;
      } else {
        const res = await axios.post(BASE_URL + '/api/about-section', submitData);
        savedData = res.data.data || res.data;
      }
      // Optimistically update local state instead of re-fetching
      if (savedData?._id) {
        setAboutSection(savedData);
        setForm(savedData);
      }
      resetUploadState();
      toast.success('About section saved successfully!');
      // Trigger preview refresh after successful save
    
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save about section';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h2 className="page-title">About Section</h2>
      </div>

      {loading ? (
        <PageLoader label="Loading about..." />
      ) : (
        <form onSubmit={handleSubmit} className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Form Fields */}
            <div className="space-y-5">
              <div className="form-group">
                <Label className="form-label">Section Heading<span className="text-red-500">*</span></Label>
                <Input 
                  name="heading" 
                  placeholder="About Artist Name, My Story, Bio" 
                  value={form.heading} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>

              <div className="form-group">
                <Label className="form-label">Highlight <span className="text-muted-foreground font-normal">(Used as the SEO meta description on your website.)</span></Label>
                <Textarea 
                  ref={subtitleRef}
                  name="subtitle1" 
                  placeholder="Write a brief, compelling summary of your work for search engines..." 
                  value={form.subtitle1} 
                  onChange={(e) => {
                    setForm(prev => ({ ...prev, subtitle1: e.target.value }));
                    adjustTextareaHeight(subtitleRef.current, 56);
                  }}
                  className="sm:resize-y min-h-[56px]" 
                  rows={2}
                />
                 <p className="text-xs text-muted-foreground">
          Spare sometime to craft a compelling highlight as it is used by search engines and can significantly enhance your website's SEO and visibility.
        </p>
              </div>

              <div className="form-group">
                <Label className="form-label">Your Story</Label>
                <Textarea 
                  ref={contentRef}
                  name="content" 
                  placeholder="Share a little about yourself — your journey, your inspiration, what drives your art…" 
                  value={form.content} 
                  onChange={(e) => {
                    setForm(prev => ({ ...prev, content: e.target.value }));
                    adjustTextareaHeight(contentRef.current, 140);
                  }}
                  className="sm:resize-y min-h-[140px]" 
                />
                <p className="text-xs text-muted-foreground">
          Write in your own voice. There's no wrong answer here.
        </p>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="space-y-5">
              <div className="form-group">
                <div className="flex items-center justify-between">
                  <Label className="form-label">About Image</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={form.aboutImageActive !== false}
                      onCheckedChange={(checked) => setForm(prev => ({ ...prev, aboutImageActive: checked }))}
                      aria-label="Toggle about image visibility"
                    />
                    <span className={form.aboutImageActive !== false ? 'status-label-active' : 'status-label-inactive'}>
                      {form.aboutImageActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                {uploadStep === 'idle' ? (
                  !form.aboutImage ? (
                    <div className="w-full">
                      <Card
                        className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('bg-muted/50');
                        }}
                        onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
                          e.currentTarget.classList.remove('bg-muted/50');
                        }}
                        onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('bg-muted/50');
                          const files = e.dataTransfer.files;
                          if (files.length > 0) {
                            const event = {
                              target: { files }
                            } as any;
                            handleFileChange(event);
                          }
                        }}
                        onClick={openFileUpload}
                      >
                        <div className="grid space-y-3">
                          <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                            <Upload className="size-5" />
                            <div>
                              Drop image here or{" "}
                              <Button
                                type="button"
                                variant="link"
                                className="text-primary hover:text-primary/80 p-0 h-auto font-normal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFileUpload();
                                }}
                              >
                                browse
                              </Button>{" "}
                              to upload
                            </div>
                          </div>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".png,.jpg,.jpeg"
                          onChange={handleFileChange}
                        />
                        <span className="text-xs text-muted-foreground mt-1 block">
                          Supported: JPG, PNG (max 2 MB) | Aspect ratio: 1:1
                        </span>
                      </Card>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Card
                        className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={openCropperForExisting}
                      >
                        <div 
                          className="relative max-h-64 w-full sm:max-w-64 rounded overflow-hidden"
                          style={{
                            backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
                            backgroundSize: '16px 16px',
                            backgroundPosition: '0 0, 8px 8px',
                            backgroundRepeat: 'repeat'
                          }}
                        >
                          <img 
                            src={form.aboutImage} 
                            alt="uploaded" 
                            className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity" 
                            title="Click to edit image"
                          />
                        </div>
                        <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                          <Upload className="size-4" />
                          <span>Click to edit or re-upload</span>
                        </div>
                      </Card>
                      <div className="flex gap-2">
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={openFileUpload}
                        >
                          <RefreshCw className="size-3.5" />
                          Change
                        </Button>
                        <Button 
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-danger"
                          onClick={() => setForm(prev => ({ ...prev, aboutImage: '' }))}
                        >
                          <Trash2 className="size-3.5" />
                          Remove
                        </Button>
                      </div>
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        className="hidden"
                      />
                    </div>
                  )
                ) : uploadStep === 'cropping' && selectedFile ? (
                  <div className="space-y-2">
                    <Card className="flex flex-col items-center border border-border">
                      <div className="w-full border-b border-border p-3">
                        <p className="text-sm font-medium text-foreground text-center">Adjust crop area (1:1 Aspect) - Drag to move, edges to resize</p>
                      </div>
                      <div className="w-full flex justify-center py-3">
                        <Cropper
                          file={selectedFile}
                          aspect={1}
                          maxImageSize={2 * 1024 * 1024}
                          keepSelection
                          onComplete={(_pixelCrop, percentCrop) => {
                            setCropPercent(percentCrop);
                          }}
                          onCrop={handleCropComplete}
                          onCropRef={cropperRef}
                        />
                      </div>
                    </Card>
                    <div className="flex gap-2">
                      <Button 
                        type="button"
                        onClick={async () => {
                          if (!cropPercent || !selectedFile) return;
                          
                          setUploading(true);
                          try {
                            // Load original image at full resolution
                            const originalUrl = URL.createObjectURL(selectedFile);
                            const originalImg = new Image();
                            
                            await new Promise((resolve, reject) => {
                              originalImg.onload = resolve;
                              originalImg.onerror = reject;
                              originalImg.src = originalUrl;
                            });
                            
                            // Convert percentage crop to pixel coordinates on original image
                            const imgWidth = originalImg.naturalWidth;
                            const imgHeight = originalImg.naturalHeight;
                            
                            const cropX = (cropPercent.x / 100) * imgWidth;
                            const cropY = (cropPercent.y / 100) * imgHeight;
                            const cropWidth = (cropPercent.width / 100) * imgWidth;
                            const cropHeight = (cropPercent.height / 100) * imgHeight;
                            
                            // Create canvas with full-resolution cropped dimensions
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');
                            
                            canvas.width = Math.round(cropWidth);
                            canvas.height = Math.round(cropHeight);
                            
                            // Draw the cropped portion from the original full-resolution image
                            ctx?.drawImage(
                              originalImg,
                              Math.round(cropX),
                              Math.round(cropY),
                              Math.round(cropWidth),
                              Math.round(cropHeight),
                              0,
                              0,
                              Math.round(cropWidth),
                              Math.round(cropHeight)
                            );
                            
                            // Convert to blob
                            const mimeType = selectedFile.type || 'image/png';
                            const croppedBlob = await new Promise<Blob>((resolve) => {
                              canvas.toBlob((blob) => {
                                resolve(blob || new Blob());
                              }, mimeType, 1.0);
                            });
                            
                            URL.revokeObjectURL(originalUrl);
                            
                            // Upload
                            const formData = new FormData();
                            const extension = selectedFile.type === 'image/jpeg' ? 'jpg' : 'png';
                            formData.append('file', croppedBlob, `cropped-about.${extension}`);
                            
                            const res = await axios.post(BASE_URL + '/api/images/upload', formData, {
                              headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'X-Domain': localStorage.getItem('selectedDomain'),
                              },
                            });
                            
                            setForm((prev) => ({ ...prev, aboutImage: res.data.url }));
                            resetUploadState();
                            const isUpdate = aboutSection?.aboutImage ? true : false;
                            toast.success(isUpdate ? 'Image updated successfully!' : 'Image uploaded successfully!');
                          } catch (err: any) {
                            console.error('Upload error:', err.response?.data || err.message);
                            setError('Image upload failed.');
                            toast.error(err.response?.data?.message || 'Image upload failed.');
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={uploading}
                        size="sm"
                        loading={uploading}
                        loadingText="Uploading..."
                      >
                        {form.aboutImage ? 'Update' : 'Upload'}
                      </Button>
                      <Button 
                        type="button"
                        variant="outline"
                        onClick={resetUploadState}
                        disabled={uploading}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {error && <div className="mt-4 p-3 bg-danger-light border border-danger/30 text-danger rounded-lg text-sm">{error}</div>}

          <div className="flex gap-2 mt-8 pt-6 border-t border-border">
            <Button type="submit" disabled={!hasChanges() || saving} loading={saving} loadingText="Saving...">
              Save Changes
            </Button>
            <Button type="button" variant="outline" onClick={handleReset} disabled={saving}>
              Reset
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AboutSectionManager;
